import React from 'react';
import PropTypes from 'prop-types';

import { Grid, Button, Typography, FormControl, FormLabel, FormGroup, FormControlLabel, Checkbox, Tooltip } from '@mui/material';
import { withStyles } from '@mui/styles';

import { FilterList, Add, Clear } from '@mui/icons-material';

import Popup from 'reactjs-popup';

import CompactTextField from './CompactTextField';
import OutlinedSelect from './OutlinedSelect';
import ParamStringBuilder from './ParamStringBuilder';
import { hasTags, getContent } from './utils';
import CompactTextFieldV2 from './CompactTextFieldV2';
import ObjParamBuilder from './ObjParamBuilder';


const scopeTags = [
	'public', 'private',
];

const groupTags = [
	'authentication',
	'session_management',
	'supporting',
	'subscription_management',
	'account_management',
	'trading',
	'market_data',
	'wallet',
];

const styles = theme => ({
	widthFix: {
		maxWidth: '100%',
	},
	widthFix2: {
		width: 'calc(100% - 32px)',
	},
	arrayGridItem: {
		width: '100%',
		paddingLeft: 16,
	},
	iconMargin: {
		paddingTop: '24px !important',
		paddingLeft: 8,
	},
	iconMargin2: {
		paddingLeft: 8,
	},
	checkbox: {
		maxHeight: 36
	},
	tagLabel: {
		textTransform: 'capitalize',
		whiteSpace: 'nowrap'
	},
	arrayInput: {
		padding: 8,
		border: theme.border,
	},
	arrayInputItem: {
		padding: '0px 8px 8px',
		border: theme.border,
	},
	arrayInputNoItems: {
		color: theme.palette.text.secondary,
		textAlign: 'center'
	},
	sendButtons: {
		borderTop: theme.border,
		marginTop: '8px !important',
		paddingTop: '8px !important',
	}
});

class RequestBuilder extends React.PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			method: null,
			params: null,
			scopeTags: [],
			groupTags: [],
		};

		this.methodChanged = this.methodChanged.bind(this);
		this.paramChanged = this.paramChanged.bind(this);
		this.onKeyDown = this.onKeyDown.bind(this);

		this.excludeTags = ['internal', 'v1'];
	}

	init() {
		let url = new URL(window.location.href);
		let include = url.searchParams.get('include');
		if (include) {
			include.split(',').forEach(tag => {
				let i = this.excludeTags.indexOf(tag);
				if (i >= 0) {
					this.excludeTags.splice(i, 1);
				}
			});
		}
		let method = url.searchParams.get('method');
		if (method) {
			if (!method.startsWith('/')) {
				method = '/' + method;
			}
			method = method.replace(/^\/+/, '/');
			url.searchParams.delete('method');
			if (this.getMethod(method)) {
				let params = {};
				url.searchParams.forEach((value, name) => {
					let parameterSchema = this.getParamOf(method, name);
					if (parameterSchema) {
						if (parameterSchema.schema.type === 'array') {
							params[name] = value.split(',').filter(Boolean);
						} else {
							params[name] = value;
						}
					}
				});
				return this.setState({ method: method, params: params });
			}
		}

		this.setState({ method: null, params: null });
	}

	send(type) {
		try {
			let method = this.state.method;
			let params = {};
			if (this.state.params) {
				Object.entries(this.state.params).forEach(([key, value]) => {
					if (Array.isArray(value)) {
						if(value[0]?.$key !== undefined) {
                            value = value.reduce((acc, {$key, $value}) => (acc[$key] = $value, acc), {});
						} else {
							value = value.map(item => item.toString());
						}
					}
					if (value !== '' && value !== null && value !== undefined) {
						let paramSchema = this.getParamOf(method, key);
						if (paramSchema) {
							if (paramSchema.schema.type === 'integer') {
								value = parseInt(value);
							} else if (paramSchema.schema.type === 'number') {
								value = parseFloat(value);
							}
						}
						params[key] = value;
					}
				});
			}
			this.props.onSend({ method: method.slice(1), params: params }, type, {method, params: {...this.state.params}});
		} catch (e) {
			console.error(e);
		}
	}

    setRequestObject({method, params}) {
        if(method && params) {
            if(!method.startsWith('/')) {
                method = '/' + method;
            }
            this.methodChanged(method, params);
        } else {
            console.error(method, params);
        }
    }

	getMethod(path) {
		if (path !== null && path !== '' && this.props.deribitApi !== null) {
			return this.props.deribitApi.paths[path];
		}
		return undefined;
	}

	methodChanged(method, newParams) {
		if (method !== this.state.method || newParams) {
			let params = null;
			if (method !== '') {
				let paramSchemas = this.getMethod(method).get.parameters;
				if (paramSchemas !== undefined) {
					params = {};
					for (let i = 0; i < paramSchemas.length; i++) {
						let param = getContent(this.props.deribitApi, paramSchemas[i]);
						let schema = getContent(this.props.deribitApi, param.schema);
						params[param.name] = (newParams && newParams[param.name]) || (schema.type === 'array' || schema.type === 'object' ? [this.defaultItem(method, param.name, schema.type)] : '');
					}
				}
			}

			this.setState({
				method: method,
				params: params,
			});
		}
	}

	paramChanged(name) {
		return (value) => {
			let params = { ...{}, ...this.state.params };
			if (value !== undefined && value !== null && value !== '') {
				params[name] = value;
            // eslint-disable-next-line no-prototype-builtins
			} else if (params.hasOwnProperty(name)) {
				delete params[name];
			}
			this.setState({ params: params });
		};
	}

	getParamOf(path, paramName) {
		let method = this.getMethod(path);
		if (method !== undefined && method.get.parameters !== undefined) {
			let parameters = method.get.parameters;
			for (let i = 0; i < parameters.length; i++) {
				let param = getContent(this.props.deribitApi, parameters[i]);
				if (paramName === param.name) {
					return param;
				}
			}
		}
		return undefined;
	}

	addArrayElem(arrayName, arrayType) {
		return () => {
			let arrayCopy = this.state.params[arrayName] ? this.state.params[arrayName].slice() : [];
			let newItem = this.defaultItem(this.state.method, arrayName, arrayType);
			arrayCopy.push(newItem);
			let params = { ...{}, ...this.state.params };
			params[arrayName] = arrayCopy;
			this.setState({ params: params });
		};
	}

	defaultItem(method, arrayName, arrayType) {
		return (
			arrayType === 'object' ?
			this.defaultObjectItem(method, arrayName) :
			this.defaultArrayItem(method, arrayName)
		);
	}

	defaultObjectItem(method, arrayName) {
		let param = this.getParamOf(method, arrayName);
		let content = getContent(this.props.deribitApi, param.schema);
		if(content.properties.$key) {
			let param = this.getParamOf(method, arrayName);
			let schema = getContent(this.props.deribitApi, param.schema);
			let $keyParam = getContent(this.props.deribitApi, schema.properties.$key);
			let $valueParam = getContent(this.props.deribitApi, schema.properties.$value);
			return {$key: '', $value: '', $keyParam, $valueParam};
		}
		return '';
	}

	defaultArrayItem(method, arrayName) {
		let param = this.getParamOf(method, arrayName);
		if (param !== undefined) {
			let content = getContent(this.props.deribitApi, param.schema);
			let deribitType = content?.items['x-deribit-type'];
			if (deribitType === 'channel') {
				let channels = this.getChannels(method.startsWith('/private'));
				let chName = Object.keys(channels).sort()[0];
				let channel = channels[chName];
				let params = channel.parameters;
				if (params !== undefined) {
					param = params.splice();
					for (let i = 0; i < params.length; i++) {
						params[i] = getContent(this.props.deribitApi, params[i]);
					}
				}
				return new ParamStringBuilder(chName, params);
			} else if(Array.isArray(content.items)) {
                // case for block_trade_trades etc
				let defaultObject = content.items.reduce((acc, el) => {
					acc[getContent(this.props.deribitApi, el)?.name] = '';
					return acc;
				}, {});
				return new ObjParamBuilder(this.props.deribitApi, content, defaultObject);
			}
		}
		return '';
	}

	removeArrayElem(arrayName, idx) {
		return () => {
			let array = this.state.params[arrayName].slice();
			if (idx >= 0 && idx < array.length) {
				array.splice(idx, 1);
				let params = { ...{}, ...this.state.params };
				params[arrayName] = array;
				this.setState({ params: params });
			}
		};
	}

	arrayElemChanged(arrayName, elemIdx) {
		return (value, subKey = undefined) => {
			let array = this.state.params[arrayName].slice();
			if(subKey === undefined) {
				array[elemIdx] = value;
			} else {
				if(array[elemIdx] instanceof ObjParamBuilder) {
					array[elemIdx].set(subKey, value);
				} else {
					array[elemIdx][subKey] = value;
				}
			}
			let params = { ...{}, ...this.state.params };
			params[arrayName] = array;
			this.setState({ params: params });
		};
	}

	getChannels(all = true) {
		if (this.props.deribitApi !== null) {
			let allChannels = this.props.deribitApi.components['x-subscriptions'];
			return all ? allChannels :
				Object.keys(allChannels)
					.filter(key => !(allChannels[key].tags || []).includes('private'))
					.reduce((acc, key) => {
						acc[key] = allChannels[key];
						return acc;
					}, {});
		}
		return [];
	}

	getInstruments() {
		return this.props.instruments;
	}

	// render

	renderArrayInput(param, arrayType='array') {
		return (
			<div className={this.props.classes.arrayInput}>
				<Grid container direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
					<Grid item>
						<Typography color="textSecondary">{param.name}</Typography>
					</Grid>
					<Grid item>
						<Tooltip title="Add"><Add color="primary" onClick={this.addArrayElem(param.name, arrayType)} cursor="pointer" /></Tooltip>
					</Grid>
				</Grid>
				<Grid container direction="column" justifyContent="center" alignItems="stretch">
					{this.state.params[param.name] && this.state.params[param.name].length > 0 ? (this.state.params[param.name].map((item, i) => (
						<Grid item key={param.name + ':' + i}>
							<div className={this.props.classes.arrayInputItem}>
								{this.renderArrayItem(item, param.name, i)}
							</div>
						</Grid>
					))) :
						<Typography variant="h6" className={this.props.classes.arrayInputNoItems}>
							No {param.name}.
						</Typography>}
				</Grid>
			</div>
		);
	}

	renderSubscriptionBuilder(subscr, name, idx, onChanged) {
		let channels = this.getChannels((this.state.method || '').startsWith('/private'));
		let templateName = subscr.template;
		let template = channels[templateName];
		if (template !== undefined) {
			return (
				<Grid container direction="column" justifyContent="flex-start" alignItems="stretch" spacing={2}>
					<Grid container item direction="row" alignItems="center">
						<Grid item xs className={this.props.classes.widthFix2}>
							<OutlinedSelect
								name={`${name}[${idx}]`}
								value={templateName}
								onChange={(value) => {
									let newTemplateName = value;
									if (newTemplateName !== templateName) {
										let parameters = channels[newTemplateName].parameters;
										onChanged(new ParamStringBuilder(newTemplateName, parameters ? parameters.map(param => getContent(this.props.deribitApi, param)) : null));
									}
								}}

								required={false}
								descr={template.description || template.summary}

								items={Object.keys(channels).sort()}
								withNone={false}
							/>
						</Grid>
						<Grid item className={this.props.classes.iconMargin2}>
							<Tooltip title="Remove"><Clear color="error" onClick={this.removeArrayElem(name, idx)} cursor="pointer" /></Tooltip>
						</Grid>
					</Grid>
					{template.parameters && template.parameters.map(param => {
						param = getContent(this.props.deribitApi, param);
						return (
							<Grid key={param.name} className={this.props.classes.arrayGridItem}>
								{this.renderParamInput(param, subscr.params[param.name], (value) => {
									subscr.params[param.name] = value;
									onChanged(subscr);
								})}
							</Grid>
						);
					})}
				</Grid>
			);
		}
	}

	renderArrayItem(item, name, idx) {
		let onChange = this.arrayElemChanged(name, idx);
		if (item instanceof ParamStringBuilder) {
			return this.renderSubscriptionBuilder(item, name, idx, onChange);
		} else if ((typeof item) === 'string') {
			return (
				<Grid container>
					<Grid xs className={this.props.classes.widthFix2} >
						<CompactTextFieldV2
							name={name + '#' + idx}
							value={item}
							onChange={(event) => onChange(event.target.value)}

							label={`${name}[${idx}]`}
							required={false}
						/>
					</Grid>
					<Grid className={this.props.classes.iconMargin}>
						<Tooltip title="Remove"><Clear color="error" onClick={this.removeArrayElem(name, idx)} cursor="pointer" /></Tooltip>
					</Grid>
				</Grid>
			);
		} else if(item instanceof ObjParamBuilder) {
			return (
				<Grid container>
					<Grid item xs className={this.props.classes.widthFix2} >
						{item.entries().map(([key, {param, value}]) => (
							<React.Fragment
								key={`${name}#${idx}.${key}`}
							>
								{this.renderParamInput(param, value, newValue => onChange(newValue, key))}
							</React.Fragment>
						))}
					</Grid>
					<Grid item className={this.props.classes.iconMargin}>
						<Tooltip title="Remove"><Clear color="error" onClick={this.removeArrayElem(name, idx)} cursor="pointer" /></Tooltip>
					</Grid>
				</Grid>
			);
		} else if (item.$key !== undefined) {
			let {$key, $value, $keyParam, $valueParam} = item;
			return (
				<Grid container>
					<Grid item xs className={this.props.classes.widthFix2} >
						<React.Fragment
							key={`${name}#${idx}.key`}
						>
							{this.renderParamInput($keyParam,   $key,   newValue => onChange(newValue, '$key'))}
							{this.renderParamInput($valueParam, $value, newValue => onChange(newValue, '$value'))}
						</React.Fragment>
					</Grid>
					<Grid item className={this.props.classes.iconMargin}>
						<Tooltip title="Remove"><Clear color="error" onClick={this.removeArrayElem(name, idx)} cursor="pointer" /></Tooltip>
					</Grid>
				</Grid>
			);
		} else {
			console.warn('invalid item: ', item);
		}
	}

	renderParamInput(param, value, onChanged) {
		value = value !== null && value !== undefined ? value : '';
		let required = param.required || false;
		let descr = param.description || param.summary || '';
		let paramContent = getContent(this.props.deribitApi, param.schema);
		if (paramContent.enum !== undefined || paramContent.enum_extra !== undefined) {
			const items = [...new Set([...(paramContent.enum || []), ...(paramContent.enum_extra || [])])].sort();
			return (<OutlinedSelect
				name={param.name}
				value={value}
				onChange={onChanged}
				label={param.name}
				required={required}
				descr={descr}
				items={items}
				withNone={true}
			/>);
		} else {
			switch (paramContent.type) {
			case 'string':
				if (param.name === 'instrument' || param.name === 'instrument_name') {
					return (<OutlinedSelect
						name={param.name}
						value={value}
						onChange={onChanged}

						label={param.name}
						required={required}
						descr={descr}

						items={this.getInstruments()} // already sorted
						withNone={false}

						creatable={true}
						isValidNewOption={inputValue => {
							return /^[A-Z]{3}-[0-9]{1,2}[A-Z]{3}[0-9]{2}-[0-9]{4}-[A-Z]$/.test(inputValue) || // eg. BTC-22FEB19-2000-P
									/^[A-Z]{3}-[0-9]{1,2}[A-Z]{3}[0-9]{2}$/.test(inputValue) || // eg. BTC-28JUN19
									/^[A-Z]{3}-PERPETUAL$/.test(inputValue) // eg. BTC-PERPETUAL
							;
						}}
					/>);
				} else {
					return (<CompactTextField
						name={param.name}
						type={param.name === 'password' ? 'password' : 'text'}
						value={value}
						onChange={(event) => onChanged(event.target.value)}

						label={param.name}
						required={required}

						helperText={descr}
					/>);
				}
			case 'boolean':
				return (<OutlinedSelect
					name={param.name}
					value={value}
					onChange={onChanged}

					label={param.name}
					required={required}
					descr={descr}

					items={[false, true]}
					withNone={!required}
				/>);
			case 'number':
				return (<CompactTextField
					type="number"
					name={param.name}
					value={value.toString()}
					onChange={(event) => onChanged(event.target.value !== '' ? Number(event.target.value) : undefined)}

					label={param.name}
					required={required}
					helperText={descr}
				/>);
			case 'integer':
				// TODO support 'minimum' and 'maximum'
				// TODO for 'integer' allow only integers
				return (<CompactTextField
					type="integer"
					name={param.name}
					value={value.toString()}
					onChange={(event) => onChanged(event.target.value !== '' ? event.target.value.split('.', 2)[0] : undefined)}

					label={param.name}
					required={required}
					helperText={descr}
				/>);
			case 'array':
				return this.renderArrayInput(param);
			case 'object':
				return this.renderArrayInput(param, 'object');
			default:
				return (<CompactTextField
					name={param.name}
					value={value}
					onChange={(event) => onChanged(event.target.value)}

					label={param.name}
					required={required}
					helperText={descr}
				/>);
			}
		}
	}

	renderProperties(pathName) {
		let method = this.getMethod(pathName);
		if (method !== undefined && method !== '') {
			let params = method.get.parameters;
			if (params !== undefined && params.length > 0) {
				return (
					<Grid container item direction="column" justifyContent="flex-start" alignItems="stretch">
						{params.map((param) => {
							param = getContent(this.props.deribitApi, param);
							return (
								<Grid item className={this.props.classes.widthFix} key={(pathName + ':' + param.name)}>
									{this.renderParamInput(param, this.state.params[param.name], this.paramChanged(param.name))}
								</Grid>
							);
						})}
					</Grid>
				);
			}
		}
		return null;
	}

	getPaths() {
		let groupTags = this.state.groupTags.slice();
		let scopeTags = this.state.scopeTags.slice();
		let paths = [];
		let pathsObject = this.props.deribitApi.paths;
		for (let pathName in pathsObject) {
			let path = pathsObject[pathName];
			if (hasTags(path.get.tags, groupTags) && hasTags(path.get.tags, scopeTags) && !hasTags(path.get.tags, this.excludeTags, 'any-exclude')) {
				paths.push({
					name: pathName,
					order: path['x-order'] || 10000,
				});
			}
		}
		return paths.sort(function (first, second) {
			if (first.name.startsWith('/private')) {
				if (second.name.startsWith('/private')) {
					return first.order < second.order ? -1 : (first.order > second.order ? 1 : first.name.localeCompare(second.name));
				} else {
					return 1;
				}
			} else {
				if (second.name.startsWith('/private')) {
					return -1;
				} else {
					return first.order < second.order ? -1 : (first.order > second.order ? 1 : first.name.localeCompare(second.name));
				}
			}
		}).map(value => value.name);
	}

	handleTagChanged(tagType, tag) {
		return (event) => {
			let tags = this.state[tagType].slice();
			let index = tags.indexOf(tag);

			if (event.target.checked) {
				if (index < 0) {
					tags.push(tag);
				}
			} else {
				if (index >= 0) {
					tags.splice(index, 1);
				}
			}
			this.setState({ [tagType]: tags });
		};
	}

	onKeyDown(event) {
		if(event.key === 'Enter' && event.ctrlKey) {
			this.send('WS');
		}
	}

	getRequestDisabled(method) {
		// TODO we should add GET support to these or mark and treat them as non-GET methods
		method = method[0] === '/' ? method : '/' + method;
		const nonGetMethods = [
			'/private/verify_block_trade',
			'/private/execute_block_trade',
			'/private/get_portfolio_margins',
		];
		return nonGetMethods.includes(method);
	}

	render() {
		let method = this.state.method || '';
		let paths = null;
		if (this.props.deribitApi !== null && this.props.deribitApi !== undefined && this.props.deribitApi.paths) {
			paths = this.getPaths();
		}
		let methodObj = this.getMethod(method);
		let descr = '';
		if (method !== '' && methodObj !== undefined) {
			descr = methodObj.get.description !== undefined ? methodObj.get.description : methodObj.get.summary;
		}
		let properties = this.renderProperties(method);
		let wsOnly = methodObj !== undefined && methodObj.get.tags !== undefined && methodObj.get.tags.includes('websocket_only');
		const settingsFormStyle = {
			width: 'auto',
			background: this.props.theme.palette.background.scrollMenu,
			borderWidth: 0,
			padding: '10px 16px 16px',
			zIndex: 10001,
		};
		return (
			<Grid container direction="column" justifyContent="center" alignItems="stretch" onKeyDown={this.onKeyDown}>
				<Grid container item justifyContent="center" alignItems="flex-start">
					<Grid item xs className={this.props.classes.widthFix2}>
						<OutlinedSelect
							name={'method'}
							value={method}
							onChange={method => this.methodChanged(method)}

							label={method}
							required={false}
							descr={descr}

							items={paths}
							withNone={false}
						/>
					</Grid>
					<Grid item className={this.props.classes.iconMargin}>
						<Popup
							arrow={false}
							overlayStyle={{ zIndex: 10000 }}
							repositionOnResize position="bottom right"
							closeOnDocumentClick
							contentStyle={settingsFormStyle}
							trigger={<div><Tooltip title="Filter"><FilterList className="icon-button" cursor="pointer" /></Tooltip></div>}
						>
							<FormControl component="fieldset">
								<FormLabel>Scope</FormLabel>
								<FormGroup>
									{scopeTags.map(tag =>
										<FormControlLabel
											className={this.props.classes.checkbox}
											key={tag}
											control={
												<Checkbox className={this.props.classes.checkbox} checked={this.state.scopeTags.indexOf(tag) >= 0} onChange={this.handleTagChanged('scopeTags', tag)} value={tag} />
											}
											label={<Typography className={this.props.classes.tagLabel}>{tag.replace(/_'/g, ' ')}</Typography>}
										/>
									)}
								</FormGroup>
								<FormLabel component="hgroup">Category</FormLabel>
								<FormGroup>
									{groupTags.map(tag =>
										<FormControlLabel
											className={this.props.classes.checkbox}
											key={tag}
											control={
												<Checkbox className={this.props.classes.checkbox} checked={this.state.groupTags.indexOf(tag) >= 0} onChange={this.handleTagChanged('groupTags', tag)} value={tag} />
											}
											label={<Typography className={this.props.classes.tagLabel}>{tag.replace(/_/g, ' ')}</Typography>}
										/>
									)}
								</FormGroup>
							</FormControl>
						</Popup>
					</Grid>
				</Grid>
				{method !== '' && properties !== null && (
					<Grid item>
						{properties}
					</Grid>
				)}
				{method !== '' &&
					(<Grid container item direction="row" justifyContent="flex-end" alignItems="center" spacing={1} className={this.props.classes.sendButtons}>
						<Grid item><Button size="small" onClick={() => this.setState({ method: null, params: null })}>Cancel</Button></Grid>
						{ this.props.service?.wsEndpoint && <Grid item><Button size="small" variant="contained" color="primary" onClick={ () => this.send('WS') }>Ws</Button></Grid> }
						{ this.props.service?.restEndpoint && !wsOnly && <Grid item><Button size="small" variant="contained" color="primary" disabled={this.getRequestDisabled(method)} onClick={() => this.send('GET')}>Get</Button></Grid>}
						{ this.props.service?.restEndpoint && !wsOnly && <Grid item><Button size="small" variant="contained" color="primary" onClick={() => this.send('POST')}>Post</Button></Grid>}
					</Grid>)}
			</Grid>
		);
	}
}

RequestBuilder.propTypes = {
	onSend: PropTypes.func,
	deribitApi: PropTypes.object,
	instruments: PropTypes.array,
	service: PropTypes.shape({
		name: PropTypes.string,
		restEndpoint: PropTypes.string,
		wsEndpoint: PropTypes.string,
		apiPath: PropTypes.string,
		allowedMethods: PropTypes.arrayOf(PropTypes.string),
	}),
	theme: PropTypes.object,
	classes: PropTypes.object,
};

export default withStyles(styles, { withTheme: true })(RequestBuilder);
